import { clsx } from "clsx";
import { type CSSProperties, forwardRef, type HTMLAttributes, type PropsWithChildren } from "react";

import { useDataAttributes } from "../../hooks.ts";
import { useDOMRef } from "../../hooks/useDomRef.ts";
import style from "./Callout.module.css";
import { CalloutIntent } from "./settings.ts";

export interface CalloutProps {
  title?: string;
  /** @default CalloutIntent.None */
  intent?: CalloutIntent;
  /** Put title and content on the same line */
  inline?: boolean;
  className?: string;
  contentClassName?: string;
  style?: CSSProperties;
  domProps?: HTMLAttributes<HTMLElement>;
  small?: boolean;
}

/**
 * Highlight important content.
 */
export const Callout = forwardRef<HTMLDivElement, PropsWithChildren<CalloutProps>>(function Callout(props, ref) {
  const calloutRef = useDOMRef(ref);
  const dataAttributes = useDataAttributes(props);

  if (props.children == null) return null;

  return (
    <div
      ref={calloutRef}
      className={clsx(style.callout, props.className, {
        [style.small]: props.small,
        [style.inline]: props.inline || props.small,
        [style.base]: props.intent == null || props.intent === CalloutIntent.None,
        [style.info]: props.intent === CalloutIntent.Info,
        [style.success]: props.intent === CalloutIntent.Success,
        [style.warning]: props.intent === CalloutIntent.Warning,
        [style.danger]: props.intent === CalloutIntent.Danger,
      })}
      style={props.style}
      data-testid={"callout"}
      {...props.domProps}
      {...dataAttributes}
    >
      {props.title != undefined && <div className={style.title}>{props.title}</div>}
      <div className={clsx(style.content, props.contentClassName)}>{props.children}</div>
    </div>
  );
});
